import React from "react";
import { Helmet } from "react-helmet-async";
import { siteConfig } from "../config/siteConfig";
import Header from "./Header";
import Footer from "./Footer";
import StructuredData from "./StructuredData";
import SupportWidget from "./SupportWidget";

const GlobalLayout = ({
  children,
  title = "",
  description = "",
  keywords = [],
  canonicalUrl,
  ogImage,
  pageType = "website",
  className = "",
  containerized = true,
  onLaunchWizard,
  structuredData = null,
}) => {
  const pageTitle = title
    ? siteConfig.seo.titleTemplate.replace("%s", title)
    : siteConfig.seo.defaultTitle;

  const pageDescription = description || siteConfig.seo.defaultDescription;

  const baseKeywords = Array.isArray(siteConfig.seo.keywords)
    ? siteConfig.seo.keywords
    : [];
  const pageKeywords = [
    ...baseKeywords,
    ...(Array.isArray(keywords) ? keywords : [keywords].filter(Boolean)),
  ];

  const currentUrl =
    canonicalUrl || (typeof window !== "undefined" ? window.location.href : "");

  const defaultOg =
    (siteConfig.seo.openGraph &&
      siteConfig.seo.openGraph.images &&
      siteConfig.seo.openGraph.images[0]) ||
    { url: "/og-image.png", width: 1200, height: 630, alt: siteConfig.brand?.name || "Site" };

  const pageOgImage = ogImage || defaultOg;

  return (
    <div
      className={`min-h-screen bg-white flex flex-col ${className}`}
      style={{
        fontFamily: siteConfig.fonts.body,
        "--color-primary": siteConfig.colors.primary,
        "--color-secondary": siteConfig.colors.secondary,
        "--font-heading": siteConfig.fonts.heading,
        "--font-body": siteConfig.fonts.body,
      }}
    >
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        {pageKeywords.length > 0 && (
          <meta name="keywords" content={pageKeywords.join(", ")} />
        )}
        {currentUrl && <link rel="canonical" href={currentUrl} />}

        {/* Open Graph */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content={pageType} />
        {currentUrl && <meta property="og:url" content={currentUrl} />}
        {siteConfig.seo.openGraph?.siteName && (
          <meta property="og:site_name" content={siteConfig.seo.openGraph.siteName} />
        )}
        {siteConfig.seo.openGraph?.locale && (
          <meta property="og:locale" content={siteConfig.seo.openGraph.locale} />
        )}
        {pageOgImage?.url && <meta property="og:image" content={pageOgImage.url} />}
        {pageOgImage?.width && (
          <meta property="og:image:width" content={String(pageOgImage.width)} />
        )}
        {pageOgImage?.height && (
          <meta property="og:image:height" content={String(pageOgImage.height)} />
        )}
        {pageOgImage?.alt && <meta property="og:image:alt" content={pageOgImage.alt} />}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        {pageOgImage?.url && <meta name="twitter:image" content={pageOgImage.url} />}

        {/* Misc */}
        <meta name="robots" content="index, follow" />
        {siteConfig.brand?.name && <meta name="author" content={siteConfig.brand.name} />}
        <meta name="theme-color" content={siteConfig.colors.primary} />

        {/* Preconnect */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      </Helmet>

      {/* Optional structured data */}
      {structuredData && (
        <StructuredData type={structuredData.type} data={structuredData.data} />
      )}
      <StructuredData type="WebSite" data={{}} />

      <Header onLaunchWizard={onLaunchWizard} />

      <main className="flex-1" role="main" id="main-content">
        {containerized ? (
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-[72px]">{children}</div>
        ) : (
          children
        )}
      </main>

      <Footer onLaunchWizard={onLaunchWizard} />

      {/* Smart FAQ Support Widget - Available on all pages */}
      <SupportWidget />
    </div>
  );
};

export default GlobalLayout;
